import React, { ChangeEvent } from 'react';
import favoriteIconEmpty from '../../../../../assets/images/favorite_empty.svg';
import favoriteIconFill from '../../../../../assets/images/favorite_fill.svg';
import {
  BoxActionContainer,
  ChipContainer,
  ChipText,
  ChipTitle,
  Container,
  ErrorBanner,
  ProductHeaderTitle,
  RowActionContainer,
  Text
} from './style';

import { Checkbox, Button, Title, Icon } from '@gerdau/hefesto/';
import { Tag } from '@gerdau/hefesto-mais-web/';
import {
  CartOptions,
  MeasureUnit,
  NewCartItem,
  UpdateItemProps
} from '../../../../../services/MVP/models';
import { formatCurrency } from '../../../../../utils/formatNumbers';
import { isDateExpired } from '../validate';
import moment from 'moment';
import {
  ChipInputs,
  DateInput,
  OptionsInput,
  QuantityInput
} from '../InputsOptions';
import { OptionsProps } from '.';
import {
  parseNumber,
  paymentAnticipated
} from '../../../../../services/MVP/utils';
import { normalizeQuantity } from '../../../../../utils/purchaseHelpers';
import { GetFlag } from '../../../../../utils/featureFlag';

interface InformationProps {
  title: string;
  text: string;
  canShow: {
    time: number;
    personalize: boolean;
    minQty: boolean;
  };
}

const SkuAgreementPriceChip = ({
  sku,
  date,
  agreement
}: {
  sku: string;
  date?: string | Date;
  agreement?: boolean;
}) => {
  return (
    <ChipContainer>
      <ChipText>Cód. Gerdau: {sku}</ChipText>
      {!date || isDateExpired(date) || !agreement ? null : (
        <ChipTitle>Preço acordado</ChipTitle>
      )}
    </ChipContainer>
  );
};

const ItemPrice = ({
  price,
  onLoadNewPrice,
  renderLoadNewPriceButton
}: {
  price: string;
  renderLoadNewPriceButton: boolean;
  onLoadNewPrice: (props: OptionsProps) => void;
}) => {
  if (!price) {
    return null;
  }

  if (renderLoadNewPriceButton) {
    return (
      <Text>
        Preço do item:{' '}
        <Button
          variant="secondary"
          onClick={() => onLoadNewPrice({ updatePrice: true })}
          name="loadNewPriceButton"
        >
          Atualizar preços
        </Button>
      </Text>
    );
  }

  return (
    <Text>
      Preço do item: <Text bold>R$ {price}</Text>
    </Text>
  );
};

const Product = ({
  selected,
  product,
  dirtyProduct,
  handleUpdateItem,
  handleSelectProduct,
  handleDuplicateItem,
  handleDeleteItem,
  isFavorited = false,
  onFavoriteToggle,
  handleUpdateAllItems,
  handleUpdateSingleItem,
  isBlockedCustomer,
  loading,
  syncingFields = new Map(),
  minDate,
  hasSomeProductsWithBenefit,
  allItemsFailed
}: {
  selected: boolean;
  product: NewCartItem;
  dirtyProduct?: NewCartItem;
  handleUpdateItem: (props: UpdateItemProps) => void;
  handleUpdateAllItems: (props: OptionsProps) => void;
  handleUpdateSingleItem?: (product: NewCartItem) => void;
  handleSelectProduct: (
    e: ChangeEvent<HTMLInputElement>,
    item_id: number
  ) => void;
  handleDuplicateItem: (id: number) => void;
  handleDeleteItem: (id: number) => void;
  isFavorited?: boolean;
  onFavoriteToggle?: (sku: string) => void;
  isBlockedCustomer: boolean;
  loading: boolean;
  syncingFields?: Map<number, Set<UpdateItemProps['type']>>;
  minDate: Date;
  hasSomeProductsWithBenefit: boolean;
  allItemsFailed?: boolean;
}) => {
  if (Object.values(product.cartOptions).length === 0) {
    return null;
  }

  const newCartEndpoint = GetFlag('new_cart_endpoints');
  const isFavoriteEnabled = GetFlag('favorite_product');

  const [information, setInformation] = React.useState({
    title: '',
    text: '',
    canShow: {
      time: 0,
      personalize: false,
      minQty: false
    }
  });

  const changeInformation = (newInformation: InformationProps) => {
    setInformation((prev) => ({
      ...prev,
      ...newInformation
    }));
  };

  const destinationOptions = Object.values(product.cartOptions).map(
    (destinationOpt: CartOptions) => ({
      value: destinationOpt.destination.code,
      label: destinationOpt.destination.description
    })
  );

  const price = product.extension_attributes.price_gerdau
    ? JSON.parse(product.extension_attributes.price_gerdau)
    : product.price;

  const destinationPrice = price.destination ?? destinationOptions[0].value;

  const destination =
    product.extension_attributes?.destination_gerdau ?? destinationPrice;

  const unity = product.extension_attributes.measure_unity_gerdau;

  const optionsByDestination = product.cartOptions[destination];

  if (!destination || !optionsByDestination || !unity) {
    return null;
  }

  const undOpt = optionsByDestination.measureUnits.map((opt) => ({
    id: opt.measure,
    label: opt.measure
  }));

  const currentMeasureUnit = optionsByDestination.measureUnits.find(
    (unit) => unit.measure === unity
  ) as MeasureUnit;

  const paymentOpt = optionsByDestination.paymentTerms
    .filter((opt) =>
      isBlockedCustomer ? paymentAnticipated.includes(opt.term) : opt
    )
    .map((opt) => ({
      value: opt.term,
      label: opt.description
    }));

  const optionsFiscalBenefits =
    optionsByDestination.fiscalBenefits.length > 0
      ? optionsByDestination.fiscalBenefits
          .map((tax) => ({
            code: tax.code,
            description:
              !tax?.description && tax?.code ? tax.code : tax?.description
          }))
          .filter((tax) => tax?.description && tax?.description?.length !== 0)
      : [];

  const taxOpt =
    optionsFiscalBenefits.length === 0
      ? [{ value: 'Indisponível', label: 'Indisponível' }]
      : [
          { code: 'nenhum', description: 'Nenhum' },
          ...optionsFiscalBenefits
        ].map((benefits) => ({
          value: benefits.code,
          label: benefits.description
        }));

  const productDate = product.extension_attributes.expected_date_gerdau
    ? product.extension_attributes.expected_date_gerdau.replace('Z', '')
    : product.extension_attributes.expected_date_gerdau;

  const disabled = loading || !destination;
  const item_id = product.item_id;
  const isFieldSyncing = (field: UpdateItemProps['type']) =>
    syncingFields.get(item_id ?? 0)?.has(field) ?? false;
  const errors = product.errors;
  const failMessage = product?.extension_attributes?.fail_message;

  const anyErrors = () => {
    if (failMessage || Object.values(errors ?? {}).some((error) => error)) {
      return 'withSpace';
    }
    return 'withoutSpace';
  };
  const salesAgreementData = price.salesAgreementData;
  //Data da industria. Foi acordado usar apenas D+1 pois no caso de CG estava vindo a data atual
  // const minDate = optionsByDestination.minimumDate
  //   ? optionsByDestination.minimumDate.replace('Z', '')
  //   : optionsByDestination.minimumDate;

  /**
   * ADCL-6410
   * Alteração na lógica para considerar a data minima
   */

  const dateMinimum = optionsByDestination.minimumDate.replace('Z', '');
  const dateMinimumFormatted =
    dateMinimum &&
    moment(dateMinimum).isValid() &&
    moment(dateMinimum).isAfter(moment(minDate))
      ? new Date(dateMinimum)
      : minDate;

  const endDate = salesAgreementData.expirationDate
    ? salesAgreementData.expirationDate.replace('Z', '')
    : salesAgreementData.expirationDate;

  const image =
    product.media_gallery_entries && product.media_gallery_entries.length > 0
      ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${product.media_gallery_entries[0]?.file}?width=56`
      : 'https://placehold.co/56x56/png';

  // const hasAgreement = product.extension_attributes?.has_agreed_price_gerdau;
  const salesAgreementExpirationDate = !moment(productDate).isAfter(endDate);

  const agreementData = {
    destination: salesAgreementData.destination,
    payment: salesAgreementData.paymentTerms,
    expirationDate: endDate
  };

  const hasAgreement = () => {
    const hasAnyAgreementKeyEmpty = Object.values(agreementData).some(
      (item) => !item
    );
    if (salesAgreementData && !hasAnyAgreementKeyEmpty) {
      return (
        salesAgreementData.destination === destination &&
        salesAgreementData.paymentTerms ===
          product.extension_attributes.payment_type_gerdau &&
        salesAgreementExpirationDate
      );
    }

    return false;
  };

  const priceWithoutMeasure =
    hasAgreement() && salesAgreementData.salesAgreement
      ? salesAgreementData.salesAgreement
      : Number(price.totalPrice ?? price.creditPrice) /
        normalizeQuantity(price.quantity);

  const convertPriceMeasure = optionsByDestination.measureUnits.find(
    (priceOpt) => priceOpt.measure === price.um
  );

  const priceKg =
    priceWithoutMeasure /
    parseNumber(convertPriceMeasure?.convMeasureKg ?? '1');

  const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
  const minimunOrderQty =
    measureUnitminQty !== 1 && product.qty === 1
      ? Math.ceil(measureUnitminQty / Number(currentMeasureUnit.multiple)) *
        Number(currentMeasureUnit.multiple)
      : Number(currentMeasureUnit.multiple) * product.qty;

  const multipleWithKG =
    parseNumber(currentMeasureUnit.convMeasureKg) * minimunOrderQty;

  const itemPrice = priceKg * multipleWithKG;

  const dirtyExtensionAttributes = dirtyProduct?.extension_attributes;
  const showLoadNewPriceButton =
    (dirtyExtensionAttributes?.expected_date_gerdau &&
      dirtyExtensionAttributes?.expected_date_gerdau !== '' &&
      moment(productDate).format('YYYY-MM-DD') !==
        moment(
          dirtyExtensionAttributes?.expected_date_gerdau.replace('Z', '')
        ).format('YYYY-MM-DD')) ||
    destination !== dirtyExtensionAttributes?.destination_gerdau ||
    product.extension_attributes.payment_type_gerdau !==
      dirtyExtensionAttributes.payment_type_gerdau ||
    product.extension_attributes.tax_benefit_gerdau !==
      dirtyExtensionAttributes.tax_benefit_gerdau ||
    (salesAgreementData && !!endDate && moment(productDate).isAfter(endDate));

  const getTaxBenefit = (product: NewCartItem) => {
    if (optionsFiscalBenefits.length === 0) return 'Indisponível';

    const benefit =
      product.extension_attributes.tax_benefit_gerdau !== undefined
        ? product.extension_attributes.tax_benefit_gerdau
        : product.price.salesAgreementData.taxBenefit
        ? optionsFiscalBenefits[0].code
        : 'nenhum';
    return benefit;
  };

  return (
    <Container
      spacing={anyErrors()}
      bgWhite
      hasError={!!failMessage && !allItemsFailed}
      key={item_id}
    >
      <RowActionContainer withoutPadding>
        <BoxActionContainer productHeader>
          <Checkbox
            name="produtos"
            checked={selected}
            onChange={(e) =>
              product.item_id && handleSelectProduct(e, product.item_id)
            }
            disabled={disabled}
          />
          <img width="56" height="56" src={image} alt={product.name} />
          <ProductHeaderTitle>
            <Tag variant={'neutral-inverse'}>
              {product.descricao_gpm?.toUpperCase()}
            </Tag>
            <Title level={4}>{product.name}</Title>
          </ProductHeaderTitle>
        </BoxActionContainer>
        <SkuAgreementPriceChip
          sku={product.sku}
          agreement={hasAgreement()}
          date={endDate}
        />
      </RowActionContainer>

      <RowActionContainer optionsGap>
        <BoxActionContainer chipStrategy>
          <ChipInputs
            label="Unidade de medida"
            disabled={disabled || isFieldSyncing('chip')}
            options={undOpt}
            onChange={handleUpdateItem}
            value={[unity ?? '']}
            item_id={item_id ?? 0}
            measureUnit={currentMeasureUnit}
            allMeasureUnit={optionsByDestination.measureUnits}
            information={information}
            changeInformation={changeInformation}
          />
        </BoxActionContainer>

        <BoxActionContainer hiddenFiscalBenefit={!hasSomeProductsWithBenefit}>
          <QuantityInput
            key={product.extension_attributes.measure_unity_gerdau}
            label="Quantidade"
            value={product.qty}
            disabled={disabled || isFieldSyncing('quantity')}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            measureUnit={currentMeasureUnit}
            units={optionsByDestination.measureUnits}
            information={information}
            changeInformation={changeInformation}
          />
          <DateInput
            error={errors?.date}
            label="Data desejada"
            value={productDate}
            minDate={dateMinimumFormatted}
            endDate={endDate ? moment(endDate).toDate() : undefined}
            disabled={disabled || selected || isFieldSyncing('date')}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            hasAgreement={hasAgreement()}
            disableWeekend
          />
          <OptionsInput
            key={product.extension_attributes.destination_gerdau}
            error={errors?.destination}
            type="destination"
            label="Destinação"
            value={destination}
            options={destinationOptions}
            disabled={loading || selected || isFieldSyncing('destination')}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            hasAgreement={hasAgreement()}
          />

          {!newCartEndpoint && (
            <OptionsInput
              key={product.extension_attributes.payment_type_gerdau}
              error={errors?.payment}
              type="payment"
              label="Pagamento"
              value={product.extension_attributes.payment_type_gerdau}
              options={paymentOpt}
              disabled={disabled || selected || isFieldSyncing('payment')}
              onChange={handleUpdateItem}
              item_id={item_id ?? 0}
              hasAgreement={hasAgreement()}
            />
          )}

          {hasSomeProductsWithBenefit && (
            <OptionsInput
              key={product.extension_attributes.tax_benefit_gerdau}
              error={errors?.taxBenefit}
              type="taxBenefit"
              label="Benefício fiscal"
              value={getTaxBenefit(product)}
              options={taxOpt}
              disabled={
                disabled ||
                optionsFiscalBenefits.length === 0 ||
                selected ||
                isFieldSyncing('taxBenefit')
              }
              onChange={handleUpdateItem}
              item_id={item_id ?? 0}
              hasAgreement={hasAgreement()}
            />
          )}
        </BoxActionContainer>
      </RowActionContainer>

      <RowActionContainer withoutPadding priceContainer>
        <ItemPrice
          price={formatCurrency(itemPrice.toFixed(2))}
          onLoadNewPrice={
            handleUpdateSingleItem && newCartEndpoint
              ? () => handleUpdateSingleItem(product)
              : handleUpdateAllItems
          }
          renderLoadNewPriceButton={showLoadNewPriceButton}
        />
        <BoxActionContainer>
          {isFavoriteEnabled && onFavoriteToggle && (
            <Button
              name="Favoritar"
              variant="secondary"
              icon={
                <img
                  src={isFavorited ? favoriteIconFill : favoriteIconEmpty}
                  alt=""
                  style={{ width: '1rem', height: '1rem' }}
                />
              }
              onClick={() => onFavoriteToggle(product.sku)}
              disabled={disabled}
            >
              {isFavorited ? 'Favoritado' : 'Favoritar'}
            </Button>
          )}
          <Button
            name="Aplicar"
            variant="secondary"
            onClick={() => item_id && handleDuplicateItem(item_id)}
            disabled={disabled}
          >
            Duplicar
          </Button>
          <Button
            name="Deletar"
            variant="danger"
            icon={<Icon option="Trash" />}
            onClick={() => item_id && handleDeleteItem(item_id)}
            disabled={disabled}
          />
        </BoxActionContainer>
      </RowActionContainer>

      {failMessage && !allItemsFailed && (
        <ErrorBanner>
          <Icon option="Alert" size="xs" />
          <span>
            Não foi possível processar este item no pedido. Remova-o e
            adicione-o novamente.
          </span>
        </ErrorBanner>
      )}
    </Container>
  );
};

export default Product;
